import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, InfoWindow, Marker, Polyline, useJsApiLoader } from '@react-google-maps/api';
import { Button, Modal, Spinner, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBatteryEmpty,
    faBatteryFull,
    faBatteryHalf,
    faBatteryQuarter,
    faBatteryThreeQuarters,
    faCalendarDay,
    faClock,
    faCar,
    faExclamationTriangle,
    faFileAlt,
    faFlagCheckered,
    faKey,
    faLocationArrow,
    faMobileAlt,
    faParking,
    faPause,
    faPlay,
    faPlayCircle,
    faRoad,
    faRoute,
    faSatelliteDish,
    faSignal,
    faStepBackward,
    faStepForward,
    faTachometerAlt,
    faThermometerHalf,
} from '@fortawesome/free-solid-svg-icons';
import dateFormat from 'dateformat';
import { DatePickerWrapper } from '../components/DatePickerWrapper';
import { User } from '../models/table-models';
import { AuthContext } from '../context/AuthContext';
import { Role } from '../utils/Enums/Roles';
import {
    DistributorLocationPoint,
    RouteBranchMarker,
    RouteBranchMarkerStatus,
    RouteStop,
    RouteSummary,
    RouteSummaryPart,
} from '../models/distributor-location-models';
import { fetchFleetUserBranchMarkers, fetchFleetUserRoute, fetchFleetUserRouteSummary } from '../utils/fleet-hooks';

// Модалът е почти на цял екран: картата взима височината, която остава над нея, но не под 520 px.
const MAP_HEIGHT = 'max(520px, calc(100vh - 470px))';

const mapContainerStyle = {
    width: '100%',
    height: '100%',
};

const stickyHeader = { position: 'sticky' as const, top: 0, zIndex: 2 };

/**
 * Последните три колони на таблицата: за телефона - батерия, обхват, температура; за колата - запалване,
 * ограничение, километраж на камерата. При смесен ден заглавието показва и двете.
 */
const pointColumns = [
    {
        phone: { icon: faBatteryFull, className: 'text-success', title: 'Заряд на батерията' },
        vehicle: { icon: faKey, className: 'text-success', title: 'Запалване', label: 'Запалв.' },
    },
    {
        phone: { icon: faSignal, className: 'text-info', title: 'Обхват и тип мрежа' },
        vehicle: { icon: faRoad, className: 'text-secondary', title: 'Ограничение на пътя', label: 'Огр.' },
    },
    {
        phone: { icon: faThermometerHalf, className: 'text-info', title: 'Температура на батерията' },
        vehicle: { icon: faCar, className: 'text-secondary', title: 'Километраж на камерата', label: 'Км' },
    },
];

const toDateInputValue = (date: Date) => dateFormat(date, 'yyyy-mm-dd');

const formatTime = (value?: string) => {
    if (!value) {
        return '-';
    }
    return dateFormat(new Date(value), 'HH:MM:ss');
};

const formatSpeed = (speed?: number) => {
    if (speed === undefined || speed === null) {
        return '-';
    }
    return `${(speed * 3.6).toFixed(1)} km/h`;
};

const formatKm = (km?: number) => (km === undefined || km === null ? '-' : `${Number(km).toFixed(1)} km`);

/** 1 ч 40 мин / 25 мин */
const formatDuration = (seconds?: number) => {
    if (seconds === undefined || seconds === null) {
        return '-';
    }
    const minutes = Math.round(seconds / 60);
    const hours = Math.floor(minutes / 60);
    return hours > 0 ? `${hours} ч ${minutes % 60} мин` : `${minutes} мин`;
};

const formatHourMinute = (value?: string) => (value ? dateFormat(new Date(value), 'HH:MM') : '-');

// Полетата на колата (GPS Areon); за точка от телефона липсват.
const isVehiclePoint = (point?: DistributorLocationPoint) => point?.source === 'VEHICLE';

const formatIgnition = (accOn?: boolean) => {
    if (accOn === undefined || accOn === null) {
        return '-';
    }
    return accOn ? 'Запалена' : 'Загасена';
};

const formatSpeedLimit = (point?: DistributorLocationPoint) =>
    point?.speedLimitKmh === undefined || point?.speedLimitKmh === null
        ? '-'
        : `${Math.round(point.speedLimitKmh)} km/h${point.speedLimitRoad ? ` · ${point.speedLimitRoad}` : ''}`;

const formatMileage = (point?: DistributorLocationPoint) =>
    point?.mileageKm === undefined || point?.mileageKm === null
        ? '-'
        : `${Math.round(point.mileageKm).toLocaleString('bg-BG')} km`;

const waybillStatusLabels: Record<string, string> = {
    draft: 'Чернова',
    signed: 'Подписан',
    approved: 'Одобрен',
};

/** Поредните точки с превишена скорост - всяка отсечка се чертае в червено над маршрута. */
const overspeedPaths = (points: DistributorLocationPoint[]) => {
    const paths: google.maps.LatLngLiteral[][] = [];
    let current: google.maps.LatLngLiteral[] = [];
    points.forEach((point) => {
        if (point.overspeed) {
            current.push(getPointPosition(point));
            return;
        }
        if (current.length > 1) paths.push(current);
        current = [];
    });
    if (current.length > 1) paths.push(current);
    return paths;
};

const createStopIcon = (ignitionOff?: boolean) => ({
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
            <rect x="2" y="2" width="20" height="20" rx="5" fill="${ignitionOff === false ? '#6c757d' : '#334155'}" stroke="#ffffff" stroke-width="2"/>
            <text x="12" y="17" text-anchor="middle" font-family="Arial" font-size="13" font-weight="700" fill="#ffffff">P</text>
        </svg>
    `)}`,
    scaledSize: new google.maps.Size(22, 22),
    anchor: new google.maps.Point(11, 11),
});

/** „кола В 9985 ВР“ / „телефон …“ - за реда на частта в обобщението. */
const partLabel = (part: RouteSummaryPart) =>
    part.source === 'VEHICLE'
        ? `Кола ${part.plateNumber ?? part.deviceIdentifier ?? ''}`
        : part.mobileDeviceId
        ? `Телефон ${part.mobileDeviceId}`
        : 'Телефон';

// Приложението праща WIFI, CELLULAR, OTHER или NONE; неизвестна стойност се показва както е дошла.
const networkTypeLabels: { [key: string]: string } = {
    WIFI: 'WiFi',
    CELLULAR: 'Мобилна',
    OTHER: 'Друга',
    NONE: 'Няма мрежа',
};

const formatBattery = (batteryLevel?: number) => {
    if (batteryLevel === undefined || batteryLevel === null || Number(batteryLevel) < 0) {
        return '-';
    }
    return `${Number(batteryLevel)}%`;
};

const getBatteryIcon = (batteryLevel?: number) => {
    if (batteryLevel === undefined || batteryLevel === null || Number(batteryLevel) < 0) {
        return faBatteryHalf;
    }
    const lvl = Number(batteryLevel);
    if (lvl >= 80) return faBatteryFull;
    if (lvl >= 50) return faBatteryThreeQuarters;
    if (lvl >= 25) return faBatteryHalf;
    if (lvl >= 10) return faBatteryQuarter;
    return faBatteryEmpty;
};

const getBatteryColorClass = (batteryLevel?: number) => {
    if (batteryLevel === undefined || batteryLevel === null || Number(batteryLevel) < 0) {
        return 'text-secondary';
    }
    const lvl = Number(batteryLevel);
    if (lvl >= 40) return 'text-success';
    if (lvl >= 20) return 'text-warning';
    return 'text-danger';
};

const getTemperatureColorClass = (temperature?: number) => {
    if (temperature === undefined || temperature === null) {
        return 'text-secondary';
    }
    const temp = Number(temperature);
    if (temp >= 45) return 'text-danger';
    if (temp >= 38) return 'text-warning';
    return 'text-info';
};

// Нивото и типът мрежа вървят заедно: 0 чертички изглежда еднакво и когато няма покритие,
// и когато телефонът е на WiFi и просто не ползва мобилната мрежа.
const formatSignal = (signalLevel?: number, networkType?: string) => {
    const network = networkType ? networkTypeLabels[networkType] ?? networkType : undefined;
    if (signalLevel === undefined || signalLevel === null) {
        return network ?? '-';
    }
    const bars = `${Number(signalLevel)}/4`;
    return network ? `${bars} · ${network}` : bars;
};

const formatBatteryTemperature = (temperature?: number) => {
    if (temperature === undefined || temperature === null) {
        return '-';
    }
    return `${Number(temperature).toFixed(1)} °C`;
};

// Записите отпреди приложението да праща ANDROID_ID нямат идентификатор. Стоят в отделна група,
// за да не изчезват при филтриране, вместо да се долепят до някой от телефоните.
const NO_DEVICE = '__none__';
const ALL_DEVICES = '';

// Точките от колата (GPS Areon) са отделно „устройство“ до телефоните: денят може да е наполовина с
// кола и наполовина с телефон, а филтърът показва всеки източник поотделно.
const VEHICLE_PREFIX = 'vehicle:';

const deviceKey = (point: DistributorLocationPoint) =>
    point.source === 'VEHICLE'
        ? `${VEHICLE_PREFIX}${point.plateNumber ?? point.deviceIdentifier ?? ''}`
        : point.mobileDeviceId ?? NO_DEVICE;

const deviceLabel = (key: string) => {
    if (key === NO_DEVICE) return 'Без идентификатор';
    if (key.startsWith(VEHICLE_PREFIX)) return `Кола ${key.substring(VEHICLE_PREFIX.length)}`;
    return key;
};

const DeviceId = ({ point }: { point?: DistributorLocationPoint }) => (
    <span className='font-monospace text-truncate d-inline-block' style={{ fontSize: '0.85em', maxWidth: '100%' }}>
        {point ? (point.source === 'VEHICLE' || point.mobileDeviceId ? deviceLabel(deviceKey(point)) : '-') : '-'}
    </span>
);

const getPointPosition = (point: DistributorLocationPoint): google.maps.LatLngLiteral => ({
    lat: Number(point.latitude),
    lng: Number(point.longitude),
});

const distanceBetweenMeters = (from: DistributorLocationPoint, to: DistributorLocationPoint) => {
    const earthRadiusMeters = 6371000;
    const fromLat = Number(from.latitude) * Math.PI / 180;
    const toLat = Number(to.latitude) * Math.PI / 180;
    const deltaLat = (Number(to.latitude) - Number(from.latitude)) * Math.PI / 180;
    const deltaLng = (Number(to.longitude) - Number(from.longitude)) * Math.PI / 180;

    const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2)
        + Math.cos(fromLat) * Math.cos(toLat) * Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return earthRadiusMeters * c;
};

const bearingBetween = (from: DistributorLocationPoint, to: DistributorLocationPoint) => {
    const toRadians = (value: number) => (value * Math.PI) / 180;
    const fromLat = toRadians(Number(from.latitude));
    const toLat = toRadians(Number(to.latitude));
    const deltaLng = toRadians(Number(to.longitude) - Number(from.longitude));
    const y = Math.sin(deltaLng) * Math.cos(toLat);
    const x = Math.cos(fromLat) * Math.sin(toLat) - Math.sin(fromLat) * Math.cos(toLat) * Math.cos(deltaLng);
    return (Math.atan2(y, x) * 180) / Math.PI + 360;
};

const getMovementBearing = (points: DistributorLocationPoint[], selectedIndex: number) => {
    const selected = points[selectedIndex];
    if (!selected) {
        return 0;
    }

    if (Number.isFinite(selected.bearing) && selected.bearing !== undefined && selected.bearing !== null) {
        return (Number(selected.bearing) + 360) % 360;
    }

    const isDifferentPoint = (point: DistributorLocationPoint) =>
        point.latitude !== selected.latitude || point.longitude !== selected.longitude;
    const nextPoint = points.slice(selectedIndex + 1).find(isDifferentPoint);
    if (nextPoint) {
        return bearingBetween(selected, nextPoint) % 360;
    }

    const previousPoint = points.slice(0, selectedIndex).reverse().find(isDifferentPoint);
    return previousPoint ? bearingBetween(previousPoint, selected) % 360 : 0;
};

const createPinIcon = (color: string) => ({
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
        <svg width="42" height="50" viewBox="0 0 42 50" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21 1C10.5 1 2 9.5 2 20c0 13.5 19 29 19 29s19-15.5 19-29C40 9.5 31.5 1 21 1Z" fill="${color}" stroke="white" stroke-width="3"/>
            <circle cx="21" cy="20" r="8" fill="white"/>
        </svg>
    `)}`,
    scaledSize: new google.maps.Size(34, 40),
    anchor: new google.maps.Point(17, 40),
    labelOrigin: new google.maps.Point(17, 20),
});

const createDirectionPointerIcon = (bearing: number) => ({
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g transform="rotate(${bearing}, 24, 24)">
                <circle cx="24" cy="24" r="18" fill="#7C3AED" fill-opacity="0.25"/>
                <path d="M24 4L36 40L24 31L12 40L24 4Z" fill="#7C3AED" stroke="#ffffff" stroke-width="2.5" stroke-linejoin="round"/>
                <circle cx="24" cy="25" r="3.5" fill="#ffffff"/>
            </g>
        </svg>
    `)}`,
    scaledSize: new google.maps.Size(42, 42),
    anchor: new google.maps.Point(21, 21),
});

const ROUTE_BRANCH_MARKER_COLORS: Record<RouteBranchMarkerStatus, string> = {
    PLANNED_AND_SERVED: '#166534',
    PLANNED_NOT_SERVED: '#dc3545',
    UNPLANNED_SERVED: '#86EFAC',
    UNPLANNED_INSTALLATION: '#86EFAC',
    UNPLANNED_MAINTENANCE: '#22C55E',
    VISITED_WITHOUT_DOCUMENT: '#d97706',
    UPCOMING_NEAR_ROUTE: '#92400E',
    PAUSED_NEAR_ROUTE: '#EAB308',
};

const routeBranchStatusLabels: Record<RouteBranchMarkerStatus, string> = {
    PLANNED_AND_SERVED: 'Обслужен по задача',
    PLANNED_NOT_SERVED: 'Пропуснато обслужване',
    UNPLANNED_SERVED: 'Обслужен без задача',
    UNPLANNED_INSTALLATION: 'Нова инсталация',
    UNPLANNED_MAINTENANCE: 'Обслужен без задача',
    VISITED_WITHOUT_DOCUMENT: 'Посетен без документ',
    UPCOMING_NEAR_ROUTE: 'Предстоящо (до 3 дни)',
    PAUSED_NEAR_ROUTE: 'Паузиран обект',
};

const createBranchPinIcon = (color: string) => ({
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
        <svg width="34" height="42" viewBox="0 0 34 42" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M17 1C8 1 1 8 1 17c0 11 16 24 16 24s16-13 16-24C33 8 26 1 17 1Z" fill="${color}" stroke="#ffffff" stroke-width="2"/>
            <circle cx="17" cy="17" r="6" fill="#ffffff" fill-opacity="0.9"/>
        </svg>
    `)}`,
    scaledSize: new google.maps.Size(28, 34),
    anchor: new google.maps.Point(14, 34),
});

const StatItem = ({
    icon,
    label,
    value,
    tone = '#0d6efd',
}: {
    icon: any;
    label: string;
    value: React.ReactNode;
    tone?: string;
}) => (
    <div
        className='border rounded bg-white px-3 py-2 d-flex align-items-center gap-2 flex-fill'
        style={{ minHeight: 58, flex: '1 1 0', minWidth: 0 }}
    >
        <span
            className='d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0'
            style={{ width: 32, height: 32, backgroundColor: `${tone}18`, color: tone }}
        >
            <FontAwesomeIcon icon={icon} />
        </span>
        <span className='d-flex flex-column lh-sm text-truncate w-100'>
            <small className='text-secondary text-truncate'>{label}</small>
            <strong className='text-truncate'>{value}</strong>
        </span>
    </div>
);

export const DistributorLocationHistoryModal = ({
    user,
    onHide,
}: {
    user: User;
    onHide: () => void;
}) => {
    const { hasPermission } = useContext(AuthContext);
    const isAdmin = hasPermission([Role.ADMIN]);

    const apiKey = `${process.env.REACT_APP_GOOGLE_API_KEY}`;
    const { isLoaded } = useJsApiLoader({ googleMapsApiKey: apiKey });
    const [selectedDate, setSelectedDate] = useState(toDateInputValue(new Date()));
    const [allPoints, setAllPoints] = useState<DistributorLocationPoint[]>([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState(ALL_DEVICES);
    const [selectedPoint, setSelectedPoint] = useState<DistributorLocationPoint>();
    const [showPointInfoWindow, setShowPointInfoWindow] = useState(false);
    const [branchMarkers, setBranchMarkers] = useState<RouteBranchMarker[]>([]);
    const [selectedBranchMarker, setSelectedBranchMarker] = useState<RouteBranchMarker>();
    const [summary, setSummary] = useState<RouteSummary>();
    const [selectedStop, setSelectedStop] = useState<RouteStop>();
    const [playbackStatus, setPlaybackStatus] = useState<'idle' | 'playing' | 'paused'>('idle');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string>();
    const [map, setMap] = useState<google.maps.Map>();

    const isPlaying = playbackStatus === 'playing';

    // Два телефона под един акаунт пишат в един и същ поток и линията по картата скача между тях, а
    // разстоянието сумира скоковете. Затова всичко под филтъра гледа само избрания апарат.
    const points = useMemo(
        () => (selectedDeviceId === ALL_DEVICES
            ? allPoints
            : allPoints.filter((point) => deviceKey(point) === selectedDeviceId)),
        [allPoints, selectedDeviceId],
    );
    const path = useMemo(() => points.map(getPointPosition), [points]);
    const center = path[0] ?? { lat: 42.6977, lng: 23.3219 };
    const firstRawPoint = points[0];
    const lastRawPoint = points[points.length - 1];
    const totalDistance = useMemo(() => {
        if (points.length < 2) {
            return 0;
        }

        return points.slice(1).reduce((sum, point, index) => {
            const previousPoint = points[index];
            return sum + distanceBetweenMeters(previousPoint, point);
        }, 0);
    }, [points]);
    // Обобщението е по източник (кола / телефон) с ключа на филтъра - при избран апарат е само неговата част.
    const visibleParts = useMemo(
        () => (summary?.parts ?? []).filter((part) => selectedDeviceId === ALL_DEVICES || part.key === selectedDeviceId),
        [summary, selectedDeviceId],
    );
    const visibleStops = useMemo(() => visibleParts.flatMap((part) => part.stops), [visibleParts]);
    const overspeedCount = visibleParts.reduce((sum, part) => sum + part.overspeeds.length, 0);
    // Колата - по пътя от GPS Areon; телефонът - по права линия. Без обобщение - по права линия, както досега.
    const distanceKm = visibleParts.length > 0
        ? visibleParts.reduce((sum, part) => sum + Number(part.distanceKm ?? 0), 0)
        : totalDistance / 1000;
    const distanceHint = visibleParts.length === 0
        ? 'по права линия между точките'
        : visibleParts.every((part) => part.distanceByRoad)
        ? 'по пътя (GPS Areon)'
        : visibleParts.some((part) => part.distanceByRoad)
        ? 'колата по пътя, телефонът по права линия'
        : 'по права линия между точките';
    const visibleWaybills = useMemo(() => {
        const plates = new Set(visibleParts.map((part) => part.plateNumber).filter(Boolean));
        return (summary?.waybills ?? []).filter(
            (waybill) => selectedDeviceId === ALL_DEVICES || (waybill.plateNumber && plates.has(waybill.plateNumber)),
        );
    }, [summary, visibleParts, selectedDeviceId]);
    const overspeedSegments = useMemo(() => overspeedPaths(points), [points]);
    const hasVehiclePoints = useMemo(() => points.some(isVehiclePoint), [points]);
    // Без точки таблицата е празна - заглавията остават тези на телефона, както досега.
    const hasPhonePoints = useMemo(() => points.length === 0 || points.some((point) => !isVehiclePoint(point)), [points]);

    // Обикновено е един телефон за целия ден. Повече от един е самото интересно: значи човекът е
    // сменил апарата по време на обиколката или е влязъл на два.
    const deviceIds = useMemo(
        () => Array.from(new Set(allPoints.map(deviceKey).filter((key) => key !== NO_DEVICE))),
        [allPoints],
    );
    const deviceOptions = useMemo(() => {
        const counts = new Map<string, number>();
        allPoints.forEach((point) => {
            const key = deviceKey(point);
            counts.set(key, (counts.get(key) ?? 0) + 1);
        });

        return Array.from(counts.entries()).map(([key, count]) => ({
            key,
            count,
            label: deviceLabel(key),
        }));
    }, [allPoints]);
    const selectedPointIndex = useMemo(() => {
        if (!selectedPoint) {
            return 0;
        }

        const pointIndex = points.findIndex((point) => point.id === selectedPoint.id);
        return pointIndex >= 0 ? pointIndex : 0;
    }, [points, selectedPoint]);
    const sliderPoint = selectedPoint ?? points[selectedPointIndex];
    const selectedPointBearing = useMemo(
        () => getMovementBearing(points, selectedPointIndex),
        [points, selectedPointIndex],
    );

    useEffect(() => {
        setSelectedPoint((current) =>
            current && points.some((point) => point.id === current.id) ? current : points[0],
        );
    }, [points]);

    // Таблицата вдясно следва избраната точка (при пускане, плъзгача, стрелките и клик на картата): редът е в
    // средата, за да се виждат и следващите точки. Клик в самата таблица не я превърта - редът е под мишката.
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const selectedFromTable = useRef(false);
    useEffect(() => {
        const container = tableContainerRef.current;
        if (selectedFromTable.current) {
            selectedFromTable.current = false;
            return;
        }
        if (!container || !selectedPoint) {
            return;
        }
        const row = container.querySelector<HTMLTableRowElement>(`tr[data-point-id="${selectedPoint.id}"]`);
        if (!row) {
            return;
        }
        // Редът е спрямо таблицата, а тя започва горе в контейнера; закаченото заглавие заема горната част.
        const headerHeight = container.querySelector('thead')?.getBoundingClientRect().height ?? 0;
        const visibleHeight = container.clientHeight - headerHeight;
        const rowCenter = row.offsetTop - headerHeight + row.offsetHeight / 2;
        container.scrollTop = Math.max(0, rowCenter - visibleHeight / 2);
    }, [selectedPoint]);

    const selectPointByIndex = useCallback(
        (index: number) => {
            const point = points[index];
            if (point) {
                setSelectedPoint(point);
            }
        },
        [points],
    );

    const goToPreviousPoint = () => {
        if (isPlaying) setPlaybackStatus('paused');
        setSelectedBranchMarker(undefined);
        selectPointByIndex(Math.max(0, selectedPointIndex - 1));
    };

    const goToNextPoint = () => {
        if (isPlaying) setPlaybackStatus('paused');
        setSelectedBranchMarker(undefined);
        selectPointByIndex(Math.min(points.length - 1, selectedPointIndex + 1));
    };

    const togglePlayPause = () => {
        setPlaybackStatus((current) => {
            if (current === 'playing') {
                return 'paused';
            }
            setSelectedBranchMarker(undefined);
            setShowPointInfoWindow(false);
            if (selectedPointIndex >= points.length - 1) {
                selectPointByIndex(0);
            }
            return 'playing';
        });
    };

    useEffect(() => {
        if (!isPlaying) {
            return;
        }

        if (selectedPointIndex >= points.length - 1) {
            setPlaybackStatus('idle');
            return;
        }

        const playbackTimer = setTimeout(() => {
            selectPointByIndex(selectedPointIndex + 1);
        }, 400);

        return () => clearTimeout(playbackTimer);
    }, [isPlaying, points.length, selectPointByIndex, selectedPointIndex]);

    const fitMapToPoints = useCallback(
        (targetMap: google.maps.Map, targetPath: google.maps.LatLngLiteral[], targetBranchMarkers?: RouteBranchMarker[]) => {
            const hasPath = targetPath.length > 0;
            const hasMarkers = !!targetBranchMarkers && targetBranchMarkers.length > 0;

            if (!hasPath && !hasMarkers) {
                return;
            }

            if (targetPath.length === 1 && !hasMarkers) {
                targetMap.setCenter(targetPath[0]);
                targetMap.setZoom(17);
                return;
            }

            const bounds = new google.maps.LatLngBounds();
            targetPath.forEach((position) => bounds.extend(position));
            if (targetBranchMarkers) {
                targetBranchMarkers.forEach((marker) => {
                    bounds.extend({ lat: Number(marker.latitude), lng: Number(marker.longitude) });
                });
            }
            targetMap.fitBounds(bounds, 48);
        },
        [],
    );

    useEffect(() => {
        if (!map || !isLoaded || isLoading) {
            return;
        }

        fitMapToPoints(map, path, isAdmin ? branchMarkers : undefined);
    }, [branchMarkers, fitMapToPoints, isAdmin, isLoaded, isLoading, map, path]);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        setError(undefined);
        setSelectedPoint(undefined);
        setSelectedBranchMarker(undefined);
        setSelectedStop(undefined);
        setSelectedDeviceId(ALL_DEVICES);
        setPlaybackStatus('idle');

        const pointsPromise = fetchFleetUserRoute(user.id ?? 0, selectedDate);
        // Без обобщение екранът работи както досега - разстоянието е по права линия.
        const summaryPromise = user.id
            ? fetchFleetUserRouteSummary(user.id, selectedDate).catch(() => undefined)
            : Promise.resolve(undefined);
        const markersPromise =
            isAdmin && user.id
                ? fetchFleetUserBranchMarkers(user.id, selectedDate).catch(() => [] as RouteBranchMarker[])
                : Promise.resolve([] as RouteBranchMarker[]);

        // Картата се показва с маршрута; обектите и обобщението се допълват, когато дойдат - те са по-бавни
        // (обектите смятат задачите и документите за деня).
        setBranchMarkers([]);
        setSummary(undefined);
        pointsPromise
            .then((pointsResult) => {
                if (cancelled) {
                    return;
                }
                setAllPoints(pointsResult);
                setSelectedPoint(pointsResult[0]);
            })
            .catch(() => {
                if (!cancelled) {
                    setError('Неуспешно зареждане на локациите.');
                    setAllPoints([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoading(false);
                }
            });
        markersPromise.then((markersResult) => {
            if (!cancelled) {
                setBranchMarkers(Array.isArray(markersResult) ? markersResult : []);
            }
        });
        summaryPromise.then((summaryResult) => {
            // При някои грешки interceptor-ът връща {} вместо да откаже.
            if (!cancelled) {
                setSummary(Array.isArray(summaryResult?.parts) ? summaryResult : undefined);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [isAdmin, selectedDate, user.id]);

    return (
        <Modal show onHide={onHide} size='xl' centered dialogClassName='route-history-modal-dialog'>
            <Modal.Header closeButton>
                <Modal.Title>
                    <div className='d-flex gap-2'>
                        <span className='text-secondary'>{user.name || user.username}</span>
                        <span className='text-secondary'>История на движение</span>
                    </div>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ backgroundColor: '#f7f8fa' }}>
                <div className='d-flex gap-2 mb-1 w-100'>
                    <StatItem
                        icon={faCalendarDay}
                        label='Ден'
                        value={
                            <span style={{ display: 'block', width: '100%' }}>
                                <DatePickerWrapper
                                    selected={selectedDate}
                                    maxDate={toDateInputValue(new Date())}
                                    onChange={(date) => {
                                        setSelectedDate(date);
                                        setPlaybackStatus('idle');
                                    }}
                                    className='fw-normal w-100'
                                />
                            </span>
                        }
                        tone='#0dcaf0'
                    />
                    <StatItem
                        icon={faMobileAlt}
                        label='Устройство'
                        value={
                            deviceOptions.length > 1 ? (
                                <select
                                    className='form-select form-select-sm fw-normal font-monospace w-100'
                                    value={selectedDeviceId}
                                    onChange={(event) => {
                                        setSelectedDeviceId(event.target.value);
                                        setPlaybackStatus('idle');
                                    }}
                                >
                                    <option value={ALL_DEVICES}>Всички ({allPoints.length})</option>
                                    {deviceOptions.map((option) => (
                                        <option key={option.key} value={option.key}>
                                            {option.label} ({option.count})
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <span className='font-monospace' style={{ fontSize: '0.85em' }}>{deviceIds[0] ? deviceLabel(deviceIds[0]) : '-'}</span>
                            )
                        }
                        tone='#fd7e14'
                    />
                    <StatItem icon={faPlayCircle} label='Начало' value={formatTime(firstRawPoint?.recordedAt)} tone='#198754' />
                    <StatItem icon={faFlagCheckered} label='Край' value={formatTime(lastRawPoint?.recordedAt)} tone='#dc3545' />
                    <StatItem
                        icon={faRoute}
                        label='Разстояние'
                        value={<span title={distanceHint}>{`${distanceKm.toFixed(2)} km`}</span>}
                        tone='#0d6efd'
                    />
                    <StatItem
                        icon={faClock}
                        label='Точки'
                        value={points.length}
                        tone='#6f42c1'
                    />
                </div>

                {error && <div className='alert alert-danger py-2'>{error}</div>}

                {(visibleParts.length > 0 || visibleWaybills.length > 0) && (
                    <div className='border rounded bg-white shadow-sm px-3 py-2 mb-1 small d-flex flex-column gap-1'>
                        {visibleParts.map((part) => (
                            <div key={part.key} className='d-flex align-items-center gap-3 flex-wrap'>
                                <strong className='d-inline-flex align-items-center gap-1'>
                                    <FontAwesomeIcon icon={part.source === 'VEHICLE' ? faCar : faMobileAlt} className='text-secondary' />
                                    {partLabel(part)}
                                </strong>
                                <span title={part.distanceByRoad ? 'по пътя (GPS Areon)' : 'по права линия между точките'}>
                                    <FontAwesomeIcon icon={faRoad} className='text-primary' /> {formatKm(part.distanceKm)}
                                    <span className='text-secondary'> {part.distanceByRoad ? 'по пътя' : 'по права линия'}</span>
                                    {part.urbanKm !== undefined && part.urbanKm !== null && (
                                        <span className='text-secondary'>
                                            {' '}(градско {formatKm(part.urbanKm)}, извън {formatKm(part.ruralKm)})
                                        </span>
                                    )}
                                </span>
                                {part.movingSeconds !== undefined && part.movingSeconds !== null && (
                                    <span>
                                        <FontAwesomeIcon icon={faTachometerAlt} className='text-primary' /> в движение{' '}
                                        <strong>{formatDuration(part.movingSeconds)}</strong>
                                    </span>
                                )}
                                <span>
                                    <FontAwesomeIcon icon={faParking} className='text-secondary' /> спирки <strong>{part.stops.length}</strong>
                                </span>
                                {part.source === 'VEHICLE' && (
                                    <span
                                        className={part.overspeeds.length > 0 ? 'text-danger' : undefined}
                                        title={`над ограничението + ${summary?.overspeedToleranceKmh ?? 10} km/h`}
                                    >
                                        <FontAwesomeIcon icon={faExclamationTriangle} /> превишения <strong>{part.overspeeds.length}</strong>
                                    </span>
                                )}
                                {part.cameraKm !== undefined && part.cameraKm !== null && (
                                    <span className='text-secondary' title='Изминатото за деня по километража на камерата'>
                                        по камерата {formatKm(part.cameraKm)}
                                    </span>
                                )}
                                {!!part.breaks && (
                                    <span className='text-warning' title='Камерата е губила връзка: километрите са долна граница'>
                                        прекъсвания {part.breaks}
                                    </span>
                                )}
                            </div>
                        ))}
                        {visibleWaybills.map((waybill) => (
                            <div key={waybill.id} className='d-flex align-items-center gap-3 flex-wrap'>
                                <strong className='d-inline-flex align-items-center gap-1'>
                                    <FontAwesomeIcon icon={faFileAlt} className='text-secondary' />
                                    Пътен лист {waybill.plateNumber ?? ''}
                                </strong>
                                <span>{waybill.start ?? '-'} – {waybill.end ?? '-'}</span>
                                <span>
                                    по таблото <strong>{waybill.odometerKm ?? '-'} km</strong>
                                    <span className='text-secondary'> ({waybill.odometerStart ?? '-'} – {waybill.odometerEnd ?? '-'})</span>
                                </span>
                                {waybill.gpsKm !== undefined && waybill.gpsKm !== null && (
                                    <span className={waybill.mismatch ? 'text-danger fw-bold' : undefined}>
                                        по GPS {formatKm(waybill.gpsKm)}
                                    </span>
                                )}
                                <span className='badge bg-light text-dark border'>{waybillStatusLabels[waybill.status] ?? waybill.status}</span>
                            </div>
                        ))}
                    </div>
                )}

                {points.length > 0 && (
                    <div className='border rounded bg-white shadow-sm px-3 py-2 mb-1'>
                        <div className='d-flex align-items-center justify-content-between gap-3 flex-wrap mb-2'>
                            <div className='d-flex align-items-center gap-2'>
                                <span
                                    className='d-inline-flex align-items-center justify-content-center rounded-circle'
                                    style={{ width: 34, height: 34, backgroundColor: '#7c3aed18', color: '#7c3aed' }}
                                >
                                    <FontAwesomeIcon icon={faLocationArrow} />
                                </span>
                                <div className='d-flex align-items-center gap-1 lh-sm'>
                                    <small className='text-secondary'>Избрана точка</small>
                                    <strong>{selectedPointIndex + 1} от {points.length}</strong>
                                </div>
                            </div>
                            <div className='d-flex gap-2 flex-wrap align-items-center small'>
                                <span className='badge bg-light text-dark border px-2 py-1 d-inline-flex align-items-center gap-1'>
                                    <FontAwesomeIcon icon={faClock} className='text-primary' />
                                    <span>{formatTime(sliderPoint?.recordedAt)}</span>
                                </span>
                                <span
                                    className={`badge border px-2 py-1 d-inline-flex align-items-center gap-1 ${sliderPoint?.overspeed ? 'bg-danger text-white' : 'bg-light text-dark'}`}
                                >
                                    <FontAwesomeIcon icon={faTachometerAlt} className={sliderPoint?.overspeed ? undefined : 'text-primary'} />
                                    <span>{formatSpeed(sliderPoint?.speed)}</span>
                                </span>
                                {isVehiclePoint(sliderPoint) ? (
                                    <>
                                        <span className='badge bg-light text-dark border px-2 py-1 d-inline-flex align-items-center gap-1' title='Ограничение на пътя'>
                                            <FontAwesomeIcon icon={faRoad} className='text-secondary' />
                                            <span>{formatSpeedLimit(sliderPoint)}</span>
                                        </span>
                                        <span className='badge bg-light text-dark border px-2 py-1 d-inline-flex align-items-center gap-1' title='Запалване'>
                                            <FontAwesomeIcon icon={faKey} className={sliderPoint?.accOn ? 'text-success' : 'text-secondary'} />
                                            <span>{formatIgnition(sliderPoint?.accOn)}</span>
                                        </span>
                                        <span className='badge bg-light text-dark border px-2 py-1 d-inline-flex align-items-center gap-1' title='Километраж на камерата'>
                                            <FontAwesomeIcon icon={faCar} className='text-secondary' />
                                            <span>{formatMileage(sliderPoint)}</span>
                                        </span>
                                        <span className='badge bg-light text-dark border px-2 py-1 d-inline-flex align-items-center gap-1' title='Сателити / мобилен сигнал на камерата'>
                                            <FontAwesomeIcon icon={faSatelliteDish} className='text-info' />
                                            <span>{sliderPoint?.satellites ?? '-'} / {sliderPoint?.gsmSignal ?? '-'}</span>
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <span className='badge bg-light text-dark border px-2 py-1 d-inline-flex align-items-center gap-1'>
                                            <FontAwesomeIcon icon={getBatteryIcon(sliderPoint?.batteryLevel)} className={getBatteryColorClass(sliderPoint?.batteryLevel)} />
                                            <span>{formatBattery(sliderPoint?.batteryLevel)}</span>
                                        </span>
                                        <span className='badge bg-light text-dark border px-2 py-1 d-inline-flex align-items-center gap-1'>
                                            <FontAwesomeIcon icon={faSignal} className='text-info' />
                                            <span>{formatSignal(sliderPoint?.signalLevel, sliderPoint?.networkType)}</span>
                                        </span>
                                        <span className='badge bg-light text-dark border px-2 py-1 d-inline-flex align-items-center gap-1'>
                                            <FontAwesomeIcon icon={faThermometerHalf} className={getTemperatureColorClass(sliderPoint?.batteryTemperature)} />
                                            <span>{formatBatteryTemperature(sliderPoint?.batteryTemperature)}</span>
                                        </span>
                                    </>
                                )}
                                <span className='badge bg-light text-dark border px-2 py-1 d-inline-flex align-items-center gap-1'>
                                    <FontAwesomeIcon icon={faMobileAlt} className='text-secondary' />
                                    <DeviceId point={sliderPoint} />
                                </span>
                            </div>
                        </div>

                        <div className='d-flex align-items-center gap-3'>
                            <div className='btn-group btn-group-sm flex-shrink-0' role='group' aria-label='Контроли за движение'>
                                <button
                                    type='button'
                                    className='btn btn-outline-secondary'
                                    title='Предишна точка'
                                    disabled={selectedPointIndex === 0}
                                    onClick={goToPreviousPoint}
                                >
                                    <FontAwesomeIcon icon={faStepBackward} />
                                </button>
                                <button
                                    type='button'
                                    className={`btn ${isPlaying ? 'btn-danger px-3' : 'btn-primary px-3'}`}
                                    title={isPlaying ? 'Пауза' : selectedPointIndex >= points.length - 1 ? 'Пусни от начало' : 'Пусни'}
                                    disabled={points.length < 2}
                                    onClick={togglePlayPause}
                                >
                                    <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} /> {isPlaying ? 'Пауза' : 'Пусни'}
                                </button>
                                <button
                                    type='button'
                                    className='btn btn-outline-secondary'
                                    title='Следваща точка'
                                    disabled={selectedPointIndex >= points.length - 1}
                                    onClick={goToNextPoint}
                                >
                                    <FontAwesomeIcon icon={faStepForward} />
                                </button>
                            </div>

                            <input
                                type='range'
                                className='form-range mb-0'
                                min={0}
                                max={Math.max(0, points.length - 1)}
                                step={1}
                                value={selectedPointIndex}
                                disabled={points.length < 2}
                                onChange={(event) => {
                                    if (isPlaying) setPlaybackStatus('paused');
                                    selectPointByIndex(Number(event.target.value));
                                }}
                            />
                        </div>
                    </div>
                )}

                {isAdmin && branchMarkers.length > 0 && (
                    <div className='d-flex align-items-center gap-3 flex-wrap mb-1 p-2 bg-white rounded border small shadow-sm'>
                        <strong className='text-secondary'>Обекти по маршрута:</strong>
                        {([
                            'PLANNED_AND_SERVED',
                            'PLANNED_NOT_SERVED',
                            'UNPLANNED_INSTALLATION',
                            'UNPLANNED_MAINTENANCE',
                            'VISITED_WITHOUT_DOCUMENT',
                            'UPCOMING_NEAR_ROUTE',
                            'PAUSED_NEAR_ROUTE',
                        ] as RouteBranchMarkerStatus[]).map((status) => {
                            const count = branchMarkers.filter((m) => m.status === status).length;
                            const color = ROUTE_BRANCH_MARKER_COLORS[status];
                            return (
                                <span key={status} className='d-inline-flex align-items-center gap-1'>
                                    <span className='d-inline-block rounded-circle' style={{ width: 10, height: 10, backgroundColor: color }} />
                                    <span>{routeBranchStatusLabels[status]}: <strong>{count}</strong></span>
                                </span>
                            );
                        })}
                    </div>
                )}

                <div className='row g-3'>
                    <div className='col-12 col-xl-8'>
                        <div className='border rounded overflow-hidden bg-white shadow-sm' style={{ height: MAP_HEIGHT }}>
                            {!isLoaded || isLoading ? (
                                <div className='h-100 d-flex align-items-center justify-content-center'>
                                    <Spinner animation='border' />
                                </div>
                            ) : points.length === 0 ? (
                                <div className='h-100 d-flex align-items-center justify-content-center text-secondary'>
                                    Няма локации за избрания ден.
                                </div>
                            ) : (
                                <GoogleMap
                                    mapContainerStyle={mapContainerStyle}
                                    center={center}
                                    zoom={13}
                                    mapTypeId='roadmap'
                                    onLoad={(loadedMap) => {
                                        setMap(loadedMap);
                                        fitMapToPoints(loadedMap, path, isAdmin ? branchMarkers : undefined);
                                    }}
                                    onUnmount={() => setMap(undefined)}
                                >
                                    <Polyline
                                        path={path}
                                        options={{
                                            strokeColor: '#0d6efd',
                                            strokeOpacity: 0.9,
                                            strokeWeight: 5,
                                        }}
                                    />
                                    {overspeedSegments.map((segment, index) => (
                                        <Polyline
                                            key={`overspeed-${index}`}
                                            path={segment}
                                            options={{ strokeColor: '#dc3545', strokeOpacity: 1, strokeWeight: 7, zIndex: 10 }}
                                        />
                                    ))}
                                    {visibleStops.map((stop) => (
                                        <Marker
                                            key={`stop-${stop.from}`}
                                            position={{ lat: stop.latitude, lng: stop.longitude }}
                                            icon={createStopIcon(stop.ignitionOff)}
                                            title={`Спирка ${formatHourMinute(stop.from)} – ${formatHourMinute(stop.to)} (${formatDuration(stop.seconds)})`}
                                            onClick={() => {
                                                setPlaybackStatus((current) => (current === 'playing' ? 'paused' : current));
                                                setSelectedBranchMarker(undefined);
                                                setShowPointInfoWindow(false);
                                                setSelectedStop(stop);
                                            }}
                                        />
                                    ))}
                                    {selectedStop && (
                                        <InfoWindow
                                            position={{ lat: selectedStop.latitude, lng: selectedStop.longitude }}
                                            onCloseClick={() => setSelectedStop(undefined)}
                                        >
                                            <div style={{ minWidth: 180, padding: 2 }} className='small'>
                                                <strong className='d-block mb-1'>
                                                    <FontAwesomeIcon icon={faParking} /> Спирка
                                                </strong>
                                                <div>
                                                    {formatHourMinute(selectedStop.from)} – {formatHourMinute(selectedStop.to)} ·{' '}
                                                    <strong>{formatDuration(selectedStop.seconds)}</strong>
                                                </div>
                                                {selectedStop.ignitionOff !== undefined && selectedStop.ignitionOff !== null && (
                                                    <div className='text-secondary'>
                                                        {selectedStop.ignitionOff ? 'с загасен двигател' : 'на място, без загасване'}
                                                    </div>
                                                )}
                                            </div>
                                        </InfoWindow>
                                    )}
                                    {firstRawPoint && (
                                        <Marker
                                            position={getPointPosition(firstRawPoint)}
                                            icon={createPinIcon('#198754')}
                                            label={{ text: 'Н', color: '#198754', fontWeight: '700', fontSize: '13px' }}
                                            title='Начало'
                                            onClick={() => {
                                                if (isPlaying) setPlaybackStatus('paused');
                                                setSelectedBranchMarker(undefined);
                                                setSelectedPoint(firstRawPoint);
                                                setShowPointInfoWindow(true);
                                            }}
                                        />
                                    )}
                                    {lastRawPoint && lastRawPoint.id !== firstRawPoint?.id && (
                                        <Marker
                                            position={getPointPosition(lastRawPoint)}
                                            icon={createPinIcon('#dc3545')}
                                            label={{ text: 'К', color: '#dc3545', fontWeight: '700', fontSize: '13px' }}
                                            title='Край'
                                            onClick={() => {
                                                if (isPlaying) setPlaybackStatus('paused');
                                                setSelectedBranchMarker(undefined);
                                                setSelectedPoint(lastRawPoint);
                                                setShowPointInfoWindow(true);
                                            }}
                                        />
                                    )}
                                    {selectedPoint && (
                                        <Marker
                                            key={`pointer-${selectedPoint.id}-${selectedPointBearing}`}
                                            position={getPointPosition(selectedPoint)}
                                            icon={createDirectionPointerIcon(selectedPointBearing)}
                                            zIndex={1000}
                                            title={`Точка #${selectedPointIndex + 1} (${formatTime(selectedPoint.recordedAt)})`}
                                            onClick={() => {
                                                setSelectedBranchMarker(undefined);
                                                setShowPointInfoWindow(true);
                                            }}
                                        />
                                    )}
                                    {isAdmin && branchMarkers.map((marker) => (
                                        <Marker
                                            key={`branch-${marker.status}-${marker.id}`}
                                            position={{ lat: Number(marker.latitude), lng: Number(marker.longitude) }}
                                            icon={createBranchPinIcon(ROUTE_BRANCH_MARKER_COLORS[marker.status])}
                                            title={marker.name}
                                            onClick={() => {
                                                setPlaybackStatus((current) => (current === 'playing' ? 'paused' : current));
                                                setSelectedBranchMarker(marker);
                                                setShowPointInfoWindow(false);
                                            }}
                                        />
                                    ))}
                                    {selectedPoint && showPointInfoWindow && (
                                        <InfoWindow
                                            position={getPointPosition(selectedPoint)}
                                            onCloseClick={() => setShowPointInfoWindow(false)}
                                        >
                                            <div style={{ minWidth: 220, maxWidth: 280, padding: '2px 0' }}>
                                                <div className='d-flex align-items-center justify-content-between border-bottom pb-1 mb-2'>
                                                    <div className='d-flex align-items-center gap-1 text-primary fw-bold'>
                                                        <FontAwesomeIcon icon={faClock} />
                                                        <span>{formatTime(selectedPoint.recordedAt)}</span>
                                                    </div>
                                                    <span className='badge bg-light text-secondary border'>
                                                        #{selectedPointIndex + 1} от {points.length}
                                                    </span>
                                                </div>

                                                <div className='d-flex flex-column gap-1 small'>
                                                    <div className='d-flex align-items-center justify-content-between py-1 px-2 rounded bg-light'>
                                                        <span className='text-secondary d-flex align-items-center gap-2'>
                                                            <FontAwesomeIcon icon={faTachometerAlt} className='text-primary' style={{ width: 14 }} />
                                                            Скорост:
                                                        </span>
                                                        <strong className={selectedPoint.overspeed ? 'text-danger' : 'text-dark'}>
                                                            {formatSpeed(selectedPoint.speed)}
                                                        </strong>
                                                    </div>

                                                    {isVehiclePoint(selectedPoint) && (
                                                        <>
                                                            <div className='d-flex align-items-center justify-content-between py-1 px-2 rounded bg-light gap-2'>
                                                                <span className='text-secondary d-flex align-items-center gap-2 text-nowrap'>
                                                                    <FontAwesomeIcon icon={faRoad} style={{ width: 14 }} />
                                                                    Ограничение:
                                                                </span>
                                                                <strong className='text-dark text-end'>{formatSpeedLimit(selectedPoint)}</strong>
                                                            </div>
                                                            <div className='d-flex align-items-center justify-content-between py-1 px-2 rounded bg-light'>
                                                                <span className='text-secondary d-flex align-items-center gap-2'>
                                                                    <FontAwesomeIcon icon={faKey} style={{ width: 14 }} />
                                                                    Запалване:
                                                                </span>
                                                                <strong className='text-dark'>{formatIgnition(selectedPoint.accOn)}</strong>
                                                            </div>
                                                            <div className='d-flex align-items-center justify-content-between py-1 px-2 rounded bg-light'>
                                                                <span className='text-secondary d-flex align-items-center gap-2'>
                                                                    <FontAwesomeIcon icon={faCar} style={{ width: 14 }} />
                                                                    Километраж:
                                                                </span>
                                                                <strong className='text-dark'>{formatMileage(selectedPoint)}</strong>
                                                            </div>
                                                            <div className='d-flex align-items-center justify-content-between py-1 px-2 rounded bg-light'>
                                                                <span className='text-secondary d-flex align-items-center gap-2'>
                                                                    <FontAwesomeIcon icon={faSatelliteDish} style={{ width: 14 }} />
                                                                    Сателити / сигнал:
                                                                </span>
                                                                <strong className='text-dark'>
                                                                    {selectedPoint.satellites ?? '-'} / {selectedPoint.gsmSignal ?? '-'}
                                                                </strong>
                                                            </div>
                                                        </>
                                                    )}

                                                    {!isVehiclePoint(selectedPoint) && (
                                                    <>
                                                    <div className='d-flex align-items-center justify-content-between py-1 px-2 rounded bg-light'>
                                                        <span className='text-secondary d-flex align-items-center gap-2'>
                                                            <FontAwesomeIcon
                                                                icon={getBatteryIcon(selectedPoint.batteryLevel)}
                                                                className={getBatteryColorClass(selectedPoint.batteryLevel)}
                                                                style={{ width: 14 }}
                                                            />
                                                            Батерия:
                                                        </span>
                                                        <strong className='text-dark'>{formatBattery(selectedPoint.batteryLevel)}</strong>
                                                    </div>

                                                    <div className='d-flex align-items-center justify-content-between py-1 px-2 rounded bg-light'>
                                                        <span className='text-secondary d-flex align-items-center gap-2'>
                                                            <FontAwesomeIcon icon={faSignal} className='text-info' style={{ width: 14 }} />
                                                            Обхват:
                                                        </span>
                                                        <strong className='text-dark'>{formatSignal(selectedPoint.signalLevel, selectedPoint.networkType)}</strong>
                                                    </div>

                                                    <div className='d-flex align-items-center justify-content-between py-1 px-2 rounded bg-light'>
                                                        <span className='text-secondary d-flex align-items-center gap-2'>
                                                            <FontAwesomeIcon
                                                                icon={faThermometerHalf}
                                                                className={getTemperatureColorClass(selectedPoint.batteryTemperature)}
                                                                style={{ width: 14 }}
                                                            />
                                                            Темп. батерия:
                                                        </span>
                                                        <strong className='text-dark'>{formatBatteryTemperature(selectedPoint.batteryTemperature)}</strong>
                                                    </div>
                                                    </>
                                                    )}

                                                    <div className='d-flex align-items-center justify-content-between py-1 px-2 rounded bg-light'>
                                                        <span className='text-secondary d-flex align-items-center gap-2'>
                                                            <FontAwesomeIcon icon={faMobileAlt} className='text-secondary' style={{ width: 14 }} />
                                                            Устройство:
                                                        </span>
                                                        <DeviceId point={selectedPoint} />
                                                    </div>
                                                </div>
                                            </div>
                                        </InfoWindow>
                                    )}
                                    {isAdmin && selectedBranchMarker && (
                                        <InfoWindow
                                            position={{ lat: Number(selectedBranchMarker.latitude), lng: Number(selectedBranchMarker.longitude) }}
                                            onCloseClick={() => setSelectedBranchMarker(undefined)}
                                        >
                                            <div style={{ minWidth: 200, maxWidth: 280, padding: 2 }}>
                                                <div className='d-flex align-items-center gap-1 mb-1'>
                                                    <span
                                                        className='d-inline-block rounded-circle'
                                                        style={{
                                                            width: 10,
                                                            height: 10,
                                                            backgroundColor: ROUTE_BRANCH_MARKER_COLORS[selectedBranchMarker.status],
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                    <strong className='text-truncate'>{selectedBranchMarker.name}</strong>
                                                </div>
                                                <div className='small mb-2'>
                                                    <span
                                                        className='badge'
                                                        style={{
                                                            backgroundColor: `${ROUTE_BRANCH_MARKER_COLORS[selectedBranchMarker.status]}18`,
                                                            color: ROUTE_BRANCH_MARKER_COLORS[selectedBranchMarker.status],
                                                            border: `1px solid ${ROUTE_BRANCH_MARKER_COLORS[selectedBranchMarker.status]}`,
                                                            fontSize: '0.8em',
                                                        }}
                                                    >
                                                        {routeBranchStatusLabels[selectedBranchMarker.status]}
                                                    </span>
                                                </div>
                                                <div className='small text-secondary'>
                                                    <div>Задачи: <strong>{selectedBranchMarker.plannedTaskCount}</strong> · Обслужвания: <strong>{selectedBranchMarker.completedTaskCount}</strong></div>
                                                    {selectedBranchMarker.visitedAt && (
                                                        <div>Посещение: <strong>{formatTime(selectedBranchMarker.visitedAt)}</strong></div>
                                                    )}
                                                    {selectedBranchMarker.city && <div>Град: {selectedBranchMarker.city}</div>}
                                                    {selectedBranchMarker.address && <div>Адрес: {selectedBranchMarker.address}</div>}
                                                </div>
                                            </div>
                                        </InfoWindow>
                                    )}
                                </GoogleMap>
                            )}
                        </div>
                    </div>
                    <div className='col-12 col-xl-4'>
                        <div ref={tableContainerRef} className='border rounded bg-white shadow-sm overflow-auto' style={{ height: MAP_HEIGHT }}>
                            <Table size='sm' hover className='mb-0'>
                                <thead className='position-sticky top-0 bg-light border-bottom' style={{ zIndex: 2 }}>
                                    <tr>
                                        <th className='bg-light text-nowrap' style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                                            <span className='d-inline-flex align-items-center gap-1'>
                                                <FontAwesomeIcon icon={faClock} className='text-primary' />
                                                <span>Час</span>
                                            </span>
                                        </th>
                                        <th className='bg-light text-nowrap' style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                                            <span className='d-inline-flex align-items-center gap-1'>
                                                <FontAwesomeIcon icon={faTachometerAlt} className='text-primary' />
                                                <span>Скорост</span>
                                            </span>
                                        </th>
                                        {pointColumns.map(({ phone, vehicle }) => (
                                            <th
                                                key={vehicle.title}
                                                className='bg-light text-nowrap'
                                                style={stickyHeader}
                                                title={
                                                    hasVehiclePoints && hasPhonePoints
                                                        ? `${phone.title} (телефон) / ${vehicle.title.toLowerCase()} (кола)`
                                                        : hasVehiclePoints
                                                        ? vehicle.title
                                                        : phone.title
                                                }
                                            >
                                                <span className='d-inline-flex align-items-center gap-1'>
                                                    {hasPhonePoints && <FontAwesomeIcon icon={phone.icon} className={phone.className} />}
                                                    {hasVehiclePoints && (
                                                        <>
                                                            {hasPhonePoints && <span className='text-secondary'>/</span>}
                                                            <FontAwesomeIcon icon={vehicle.icon} className={vehicle.className} />
                                                            {!hasPhonePoints && <span>{vehicle.label}</span>}
                                                        </>
                                                    )}
                                                </span>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {points.map((point) => (
                                        <tr
                                            key={point.id}
                                            data-point-id={point.id}
                                            className={selectedPoint?.id === point.id ? 'table-primary cursor-pointer' : 'cursor-pointer'}
                                            onClick={() => {
                                                setSelectedBranchMarker(undefined);
                                                if (isPlaying) setPlaybackStatus('paused');
                                                selectedFromTable.current = selectedPoint?.id !== point.id;
                                                setSelectedPoint(point);
                                            }}
                                        >
                                            <td className='text-nowrap'>{formatTime(point.recordedAt)}</td>
                                            <td className={`text-nowrap ${point.overspeed ? 'text-danger fw-bold' : ''}`}>{formatSpeed(point.speed)}</td>
                                            {isVehiclePoint(point) ? (
                                                <>
                                                    <td className='text-nowrap' title='Запалване'>{formatIgnition(point.accOn)}</td>
                                                    <td className='text-nowrap' title={point.speedLimitRoad ?? 'Ограничение'}>
                                                        {point.speedLimitKmh === undefined || point.speedLimitKmh === null ? '-' : Math.round(point.speedLimitKmh)}
                                                    </td>
                                                    <td className='text-nowrap' title='Километраж на камерата'>{formatMileage(point)}</td>
                                                </>
                                            ) : (
                                                <>
                                                    <td className='text-nowrap'>{formatBattery(point.batteryLevel)}</td>
                                                    <td className='text-nowrap'>{formatSignal(point.signalLevel, point.networkType)}</td>
                                                    <td className='text-nowrap'>{formatBatteryTemperature(point.batteryTemperature)}</td>
                                                </>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button onClick={onHide}>Затвори</Button>
            </Modal.Footer>
        </Modal>
    );
};
